import React, { useState, useRef, useEffect } from "react";
import Button from "../../Common/Button";
import { handleApproveReject, saveDraftAndReviewAPI, SaveDraftPayload } from "../../Api/MarketingApi";
import { FaMapMarkerAlt } from "react-icons/fa";
import Doctor from "../../../images/doctorAvtar.png";
import { Toast } from "../../Common/Toast";
import { MarketingFormData } from "../../Types/marketing";
import { getLocationAPI } from "../../Api/LookUp";

/* ================= TYPES ================= */
interface RoleDescription {
  role: string;
  description: string;
}

interface Location {
  LocationId: number;
  LocationName: string;
  HospitalName?: string;
}

interface PreviewModalProps {
  isOpen: boolean;
  doctorImage?: string;
  data: MarketingFormData;
  payload: SaveDraftPayload | null; 
  doctorId: number; 
  onClose: () => void;
  onSubmit: () => void;
  onModalClose: () => void;
}

/* ================= COMPONENT ================= */
const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  data,
  payload, 
  doctorId, 
  doctorImage,
  onClose,
  onSubmit,
  onModalClose
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [secondaryLocationNames, setSecondaryLocationNames] = useState<{[key: number]: string}>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  const roleName = sessionStorage.getItem("roleName");

  // Fetch secondary location names - MOVED BEFORE THE RETURN
  useEffect(() => {
    const fetchLocationNames = async () => {
      if (!isOpen || !data.secondaryLocations || data.secondaryLocations.length === 0) return;
      
      try {
        const response = await getLocationAPI();
        
        if (response.data && Array.isArray(response.data)) {
          const locationMap: {[key: number]: string} = {};
          
          response.data.forEach((location: Location) => {
            if (data.secondaryLocations?.includes(location.LocationId)) {
              locationMap[location.LocationId] = location.LocationName;
            }
          });
          
          setSecondaryLocationNames(locationMap);
        }
      } catch (error) {
        console.error("Error fetching location names:", error);
      }
    };

    fetchLocationNames();
  }, [isOpen, data.secondaryLocations]);

  // Map data to doctordata structure expected by tabs
  const doctordata = {
    profileBrief: data.summary || data.benefits ? [data.summary || "", data.benefits || ""].filter(Boolean) : [],
    previousExperiance: data.previousExperiance || [],
    awardsAchievements: data.awards || [],
    researchPublications: data.publications.join('\n'),
    areaOfInterest: data.areaOfInterest || [],
    registrationNo: data.registrationNo ? [data.registrationNo] : []
  };

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleRejectClick = () => {
    setShowFeedback(true);
  };

  const handleConfirmReject = async () => {
    if (!feedback.trim()) {
      alert("Please provide feedback before Send Back");
      return;
    }

    if (!payload) {
      console.error("No payload available");
      return;
    }

    try {
      const rejectPayload: SaveDraftPayload = {
        ...payload,
        status: "SB", 
        feedback: feedback,
        Remark:"Send back for Update the Profile",
        Event:"Send Back"
      };

      const response = await handleApproveReject(rejectPayload);
      Toast.success("Send Back successfully");
      setShowFeedback(false);
      setFeedback("");

      onClose();
      onModalClose();
    } catch (error){
      console.error("error in reject", error);
      Toast.warning("Failed to send Back");
    }
  };

  const handleCancelReject = () => {
    setShowFeedback(false);
    setFeedback("");
  };

  const handleApprove = async () => {
    if (!payload) {
      console.error("No payload available");
      return;
    }
    
    try {
      const approvePayload: SaveDraftPayload = {
        ...payload, 
        status: "A", 
        feedback: "",
        Remark:"Approve the Doctor Profile",
        Event:"Approve Profile"
      };

      const response = await handleApproveReject(approvePayload);
      Toast.success("Doctor approved successfully");
      
      onClose();
      onModalClose();
    } catch (error) {
      console.error("error in approval", error);
      Toast.warning("Failed to approve content");
    }
  };

  const handleSubmit = async () => {
    try {
      await onSubmit();
      onClose();
      onModalClose();
    } catch (error) {
      console.error("Error in submit:", error);
    }
  };

  // CONDITIONAL RETURN AFTER ALL HOOKS
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 animate-fadeIn">
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden shadow-2xl border-2 border-blue-200 animate-slideUp">
        {/* HEADER */}
        <div className="sticky top-0 bg-gradient-to-r from-[#184A84] via-[#2563EB] to-[#3B82F6] text-white px-8 py-2 shadow-lg z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="text-xl font-bold">Edited Preview</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-md p-2 rounded-xl transition-all hover:rotate-90"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex ml-10">
          <div className="rounded-lg overflow-hidden flex justify-center items-center w-36 max-w-60 sm:max-w-50 md:max-w-60 lg:max-w-60">
            <img 
              src={doctorImage || Doctor}
              alt="Doctor" 
              className="w-full object-cover cursor-pointer"
              onError={(e) => {
                (e.target as HTMLImageElement).src = Doctor;
              }}
            />
          </div>

          <div className="w-full md:w-[70%] px-4 md:px-5 flex flex-col gap-1 text-left justify-center">
            <div className="font-semibold text-lg text-black capitalize">
              {data.namePrefixCode ? `${data.namePrefixCode} ${data.name}` : data.name}
            </div>
            <div className="text-gray-500 text-sm">
              {data.departmentName}
            </div>
            <div className="inline-block border border-[#009A85] px-3 py-0.5 rounded-lg text-[#009A85] text-xs w-fit">
              {`${data.experience}+ Years experience`}
            </div>
            <div className="text-gray-500 text-xs">
              {data.qualification
                ?.split(';')
                .map((item: any) => item.trim())
                .filter(Boolean)
                .map((item: any, index: any) => (
                  <div key={index}>{item}</div>
                )) || <span className="text-sm">No qualifications listed</span>}
            </div>

            <div className="flex flex-wrap gap-2">
              {data?.languages
                ? data.languages.split(",").map((lang: any, idx: any) => (
                    <span
                      key={idx}
                      className="border border-[#184A84] px-2 py-0.5 rounded-lg text-xs md:text-[12px] text-[#184A84]"
                    >
                      {lang.trim()}
                    </span>
                  ))
                : <span className="text-sm">No languages listed</span>}
            </div>

       
            {/* Primary Location */}
              <p className="text-xs font-semibold text-gray-600 uppercase ">Primary Location</p>
<div className="flex flex-row items-center sm:items-center gap-4 sm:gap-3 max-w-md w-fit rounded-lg border border-[#0099A8] px-4 sm:px-3">
  <div className="flex items-center gap-3 sm:gap-2 text-[#184A84] text-sm sm:text-[12px]">
    <div className="w-3 h-3 rounded-full border-2 border-[#0099A8] flex items-center justify-center">
      <div className="w-1 h-1 rounded-full bg-[#0099A8]" />
    </div>
  </div>
  <span className="text-xs text-[#184A84]">Rama Hospital-{data.locationName}</span>
  <div className="ml-auto p-2 sm:p-1 rounded-full bg-white shadow-sm">
    <div className="flex items-center gap-2 text-sm text-[#184A84]">
      <FaMapMarkerAlt className="w-3 h-3 text-[#0099A8]" />
    </div>
  </div>
</div>

{/* Secondary Locations */}
{data.secondaryLocations && data.secondaryLocations.length > 0 && (
  <div className="">
    <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Secondary Locations</p>
    <div className="flex flex-wrap gap-2">
      {data.secondaryLocations.map((locId, idx) => (
        <div
          key={idx}
          className="flex flex-row items-center sm:items-center gap-3 sm:gap-2 w-fit rounded-lg border border-[#0099A8] px-3 sm:px-2 py-0.5"
        >
          <div className="flex items-center gap-2 sm:gap-1 text-[#0099A8] text-sm sm:text-[12px]">
            <div className="w-3 h-3 rounded-full border-2 border-[#0099A8] flex items-center justify-center">
              <div className="w-1 h-1 rounded-full bg-[#0099A8]" />
            </div>
          </div>
          <span className="text-xs text-[#184A84] font-medium">
            {secondaryLocationNames[locId] || `Location ID: ${locId}`}
          </span>
          <div className="ml-auto p-1 rounded-full bg-white shadow-sm">
            <FaMapMarkerAlt className="w-3 h-3 text-[#0099A8]" />
          </div>
        </div>
      ))}
    </div>
  </div>
)}

          </div> 
        </div>

        {/* NEW TABBED NAVIGATION SYSTEM */}
        <div className="relative px-5 mt-3 mb-3">
          <div className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth custom-scrollbar px-8">
            {doctordata?.profileBrief.length > 0 && (
              <button
                onClick={() => scrollToSection("Overview")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Overview
              </button>
            )}
            {(doctordata?.previousExperiance?.length ?? 0) > 0 && (
              <button
                onClick={() => scrollToSection("Experience")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Experience
              </button>
            )}
            {(doctordata?.awardsAchievements?.length ?? 0) > 0 && (
              <button
                onClick={() => scrollToSection("Awards & Achievements")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Awards & Achievements
              </button>
            )}
            {doctordata?.researchPublications && (
              <button
                onClick={() => scrollToSection("Research & Publication")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Research & Publication
              </button>
            )}
            {(doctordata?.areaOfInterest?.length ?? 0) > 0 && (
              <button
                onClick={() => scrollToSection("Areas of Interest")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Areas of Interest
              </button>
            )}
            {doctordata?.registrationNo?.length > 0 && (
              <button
                onClick={() => scrollToSection("Registration No")}
                className="px-4 py-1 border border-[#009A85] rounded-full text-sm font-medium text-[#009A85] whitespace-nowrap hover:bg-[#009A85] hover:text-white transition"
              >
                Registration No
              </button>
            )}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4 px-5 xs:px-10 max-h-[40vh] overflow-y-auto">
          {(doctordata?.profileBrief?.length ?? 0) > 0 && (
            <div ref={(el) => { sectionRefs.current["Overview"] = el }} className="scroll-mt-[160px] bg-gray-100 px-5 rounded-lg py-4">
              <h2 className="text-xl font-semibold mb-2 text-[#184A84]">Overview</h2>
              <div className="mx-5 space-y-1 text-gray-700 text-justify">
                {doctordata.profileBrief.flatMap((item: string) => 
                  item.split(';').map((subItem: string) => subItem.trim()).filter(Boolean)
                ).map((item: string, index: number) => (
                  <div key={index}>{item}</div>
                ))}
              </div>
            </div>
          )}

          {(doctordata?.previousExperiance?.length ?? 0) > 0 && (
            <div ref={(el) => { sectionRefs.current["Experience"] = el }} className="scroll-mt-[10px] p-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
              <h2 className="text-[15px] font-semibold mb-1 text-[#184A84]">Experience</h2>
              <ul className="">
                {doctordata.previousExperiance.map((exp, i) => (
                  <li key={i} className="text-[13px] list-disc ml-6 text-justify">
                    <p className="font-medium">{exp.role}</p>
                    <p className="text-gray-600">{exp.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(doctordata?.awardsAchievements?.length ?? 0) > 0 && (
            <div ref={(el) => { sectionRefs.current["Awards & Achievements"] = el }} className="scroll-mt-[10px] p-2 rounded-lg bg-gradient-to-r from-yellow-50 to-orange-50">
              <h2 className="text-[15px] font-semibold mb-1 text-[#184A84]">Awards & Achievements</h2>
              <ul className="list-disc ml-5 space-y-1 text-gray-700">
                {doctordata.awardsAchievements.map((award, i) => (
                  <li key={i} className="text-[13px] list-disc">
                    <p className="text-gray-600">{award}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {doctordata?.researchPublications?.trim() && (
            <div
              ref={(el) => { sectionRefs.current["Research & Publication"] = el }}
              className="scroll-mt-[10px] px-5 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50"
            >
              <h2 className="text-[15px] font-semibold mb-1 text-[#184A84]">Research & Publication</h2>
              <p className="text-gray-700 text-[13px]">{doctordata.researchPublications}</p>
            </div>
          )}

          {(doctordata?.areaOfInterest?.length ?? 0) > 0 && (
            <div ref={(el) => { sectionRefs.current["Areas of Interest"] = el }} className="scroll-mt-[10px] p-2 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50">
              <h2 className="text-[15px] font-semibold mb-1 text-[#184A84]">Areas of Interest</h2>
              <ul className="">
                {doctordata.areaOfInterest.map((area, i) => (
                  <li key={i} className="text-[13px] list-disc ml-6">
                    <p className="font-medium">{area.role}</p>
                    <p className="text-gray-600">{area.description}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          
            {(doctordata?.registrationNo?.length ?? 0) > 0 && (
            <div 
              ref={(el) => { sectionRefs.current["Registration No"] = el }} 
              className="scroll-mt-[10px] p-2 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50"
            >
              <h2 className="text-[15px] font-semibold mb-1 text-[#184A84]">Registration No</h2>
              <p className="text-gray-700 text-[13px] font-semibold">{data.registrationNo}</p>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-100 via-blue-100 to-indigo-100 px-8 py-2 border-t-2 border-blue-300 shadow-lg">
          {showFeedback ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Send Back Feedback <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Please provide detailed feedback for send back..."
                  className="w-full px-4 py-3 border-1 border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all resize-none text-sm"
                  rows={4}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelReject}
                  className="px-6 py-1 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReject}
                  className="px-6 py-1 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-400 font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Confirm Send Back
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 text-white p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Ready to Submit?</p>
                  <p className="text-xs text-gray-600">Review all information before finalizing</p>
                </div>
              </div>

              <div className="flex gap-3 md:mt-2">
                <button
                  onClick={onClose}
                  className="px-4 py-1 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Edit
                </button>
                
                {roleName === "Content Creator" ? (
                  <button
                    onClick={handleSubmit}
                    className="px-6 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Submit for Review
                  </button>
                ) : (
                  <>
                    {roleName === "Marketing Head" && (
                      <>
                        <button
                          onClick={handleRejectClick}
                          className="px-6 py-1 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-600 font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Send Back
                        </button>

                        <button
                          onClick={handleApprove}
                          className="px-6 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Approve
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
